import { formatUTCDateToISODateString } from '@fruitful/fruitils';
import { differenceInDays, format, parseISO } from 'date-fns';
import { useAtomValue } from 'jotai';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    H2Regular,
    H6Emphasized,
    Input,
    LabelRegular,
    SmoothieScrollView,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { ConfirmModal } from '../../common/components/ConfirmModal';
import { Button } from '../../common/components/GroveButtons';
import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import { centsToFormattedDollars } from '../../common/utils/formatting';
import {
    stripeCustomerDashboardUrl,
    syncteraAccountUrl,
    syncteraPostedTransactionUrl,
} from '../../common/utils/LinkUtils';
import { adminUserAtom } from '../../entities/entityAtoms';
import { requestBuilder } from '../../http/RequestBuilder';

const useGetDirectDepositRewardSummary = requestBuilder.query<
    DirectDepositRewardDataTables,
    Record<string, never>
>({
    endpoint: () => `/api/admin/bank-ops/direct-deposit-reward/summary`,
    queryKey: () => `/api/admin/bank-ops/direct-deposit-reward/summary`,
});

type PayoutMode =
    | 'post-reward'
    | 'log-non-qualification'
    | 'log-non-qualification-unexpected';

export interface DirectDepositRewardDataTables {
    readyToBePaid: DirectDepositRewardContext[];
    doesNotQualifyAndPeriodClosed: DirectDepositRewardContext[];
    qualificationPending: DirectDepositRewardContext[];
    rewardDecisionLogged: RewardTrackingDisplayDto[];
}

interface DirectDepositRewardContext {
    program_member_type: 'new' | 'legacy';
    // Membership details
    membership_id: string;
    primary_user_id: string;
    primary_name: string;
    primary_cancellation_date?: string | null;
    partner_user_id?: string | null;
    partner_name?: string | null;
    partner_cancellation_date?: string | null;
    membership_created_at: string;
    stripe_customer_id: string;
    stripe_status?: string | null;
    // -------------------------------
    // New program member type fields
    // -------------------------------
    // First bank account created for the membership, used for debugging, this is when the 60-day qualification window clock starts
    first_bank_account_created_at: string;
    first_bank_account_id: string;
    first_synctera_account_id: string;
    // Money map qualification date, relevant for new member type only
    // A money map needs to be finalized by a guide (after a roadmap meeting)
    // within 60 days of the first bank account being opened (qualification period ends)
    money_map_finalized_for_action_at?: string | null;
    // -------------------------------------------------------------------------
    // Qualification fields relevant to the new and legacy program member types
    // -------------------------------------------------------------------------
    // New members: starts when a bank account is opened
    // Legacy members: starts at DIRECT_DEPOSIT_250_REWARD_PROGRAM_LAUNCH_UTC
    dd_range_start: string | Date;
    // New members: ends 60 days after dd_range_start (bank account opened)
    // Legacy members: ends at DIRECT_DEPOSIT_250_REWARD_PROGRAM_END_UTC
    dd_range_end: string | Date;
    // When qualification hasn't been hit this is the DD posted_incoming_ach_transactions created_at field
    // When direct deposit qualification has been reached this is the create_at of the transaction that tips the membership into qualification
    latest_dd_created_at?: string | null;
    cumulative_sum_30_days?: number | null;
    count_dd_trx_30_days: number;
    json_agg_dd_trx?: DirectDepositTransaction[] | null;
    // The account payout will go to; this is the synctera account for the transaction that triggers qualification
    qualifying_direct_deposit_synctera_account_id?: string | null;
    // Logic differs by program member type (new vs legacy)
    qualifies_for_dd_bonus: boolean;
    qualification_period_open: boolean;
    // When all qualification events have been met (logic differs by program member type)
    qualifies_at?: string | null;

    // Used for grove front-end handling only, not passed from backend
    decision_logged_in_grove?: boolean;
}

interface DirectDepositTransaction {
    id: string;
    synctera_account_id: string;
    created_at: string;
    posted_date: string;
    transaction_uuid: string;
    description?: string;
    amount_cents: number;
    dc_sign: string;
    source_account_name: string;
    source_company_name: string;
}

export const BankOpsDirectDepositRewardsPanel = () => {
    const {
        data: directDepositRewardSummary,
        isLoading: isLoadingRewardMonthSummary,
    } = useGetDirectDepositRewardSummary({});

    if (isLoadingRewardMonthSummary || directDepositRewardSummary == null) {
        return <Spinner />;
    }

    return (
        <YStack gap={16}>
            <XStack justifyContent="space-between" width="100%">
                <H6Emphasized>
                    1. Qualifies for direct deposit bonus but no reward posted
                </H6Emphasized>
            </XStack>
            <DirectDepositRewardSummaryTable
                data={directDepositRewardSummary.readyToBePaid}
            />

            <XStack justifyContent="space-between" width="100%">
                <H6Emphasized>
                    2. Qualification period close - 60 days after the first bank
                    account was opened without membership meeting criteria for
                    new members
                </H6Emphasized>
            </XStack>
            <DirectDepositRewardSummaryTable
                data={directDepositRewardSummary.doesNotQualifyAndPeriodClosed}
            />

            <XStack justifyContent="space-between" width="100%">
                <H6Emphasized>
                    3. Pending memberships - have a bank account but not met all
                    qualification criteria
                </H6Emphasized>
            </XStack>
            <DirectDepositRewardSummaryTable
                data={directDepositRewardSummary.qualificationPending}
            />

            <XStack justifyContent="space-between" width="100%">
                <H6Emphasized>
                    4. Memberships that have been decided (may or may not have
                    received a direct deposit bonus payout)
                </H6Emphasized>
            </XStack>
            <DirectDepositRewardDecisionLoggedTable
                data={directDepositRewardSummary.rewardDecisionLogged}
            />
        </YStack>
    );
};

const DirectDepositRewardSummaryTable = ({
    data,
}: {
    data: DirectDepositRewardContext[];
}) => {
    if (data.length === 0) {
        return <BodyMediumRegular>No rows</BodyMediumRegular>;
    }

    const [selectedNotFinalizedReward, setSelectedNotFinalizedReward] =
        useState<DirectDepositRewardContext | null>(null);

    return (
        <>
            <DataTable
                dataKey="membership_id"
                value={data}
                size="small"
                tableStyle={{ width: '100%' }}
                selectionMode="single"
                selection={selectedNotFinalizedReward}
                onSelectionChange={(e) => {
                    setSelectedNotFinalizedReward(
                        e.value as DirectDepositRewardContext | null,
                    );
                }}
                isDataSelectable={(_) => true}
            >
                <Column
                    header="Row"
                    style={{ width: '4rem', verticalAlign: 'top' }}
                    body={(_, options) => {
                        return <>{options.rowIndex + 1}</>;
                    }}
                />
                <Column
                    field="membership_id"
                    header="Membership"
                    style={{
                        width: '4rem',
                        verticalAlign: 'top',
                    }}
                    body={(x: DirectDepositRewardContext) => (
                        <YStack
                            // Adds visual indication in the UI when a payout has been made but the page has not been refreshed
                            backgroundColor={
                                x.decision_logged_in_grove ? 'red' : 'unset'
                            }
                        >
                            <BodySmallRegular style={{ fontSize: '0.75rem' }}>
                                Program member type:{' '}
                                {x.program_member_type === 'new' ? (
                                    <BodySmallEmphasized color="$green900">
                                        {x.program_member_type}
                                    </BodySmallEmphasized>
                                ) : (
                                    <BodySmallEmphasized color="$red900">
                                        {x.program_member_type}
                                    </BodySmallEmphasized>
                                )}
                                <br />
                                MembershipId: {x.membership_id}
                                <br />
                                Membership created: {x.membership_created_at}
                                <br />
                                Primary member: {x.primary_name}
                                <br />
                                {x.partner_user_id != null && (
                                    <>
                                        Partner member: {x.partner_name}
                                        <br />
                                    </>
                                )}
                                <br />
                                Stripe:{' '}
                                <a
                                    href={stripeCustomerDashboardUrl(
                                        x.stripe_customer_id,
                                    )}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {x.stripe_customer_id}
                                </a>
                                <br />
                                Stripe status: {x.stripe_status ?? 'UNKNOWN'}
                                <br />
                            </BodySmallRegular>
                        </YStack>
                    )}
                />
                <Column
                    header="Qualification"
                    style={{ width: '4rem', verticalAlign: 'top' }}
                    body={(x: DirectDepositRewardContext) => (
                        <BodySmallRegular style={{ fontSize: '0.75rem' }}>
                            Qualifies at:{' '}
                            {formatQualification(
                                x.qualifies_at ?? 'n/a',
                                x.qualifies_for_dd_bonus,
                            )}
                            {x.qualifies_for_dd_bonus &&
                                x.qualifies_at != null && (
                                    <>
                                        <br />
                                        <BodySmallEmphasized>
                                            &nbsp;&nbsp;&nbsp;&nbsp;
                                            {differenceInDays(
                                                new Date(),
                                                new Date(x.qualifies_at),
                                            )}{' '}
                                            days ago
                                        </BodySmallEmphasized>
                                    </>
                                )}
                            <br />
                            <br />
                            Qualification period:{' '}
                            {x.qualification_period_open ? 'open' : 'closed'}
                            {!x.qualification_period_open &&
                                x.qualifies_at != null && (
                                    <>
                                        <br />
                                        <BodySmallEmphasized>
                                            &nbsp;&nbsp;&nbsp;&nbsp;
                                            {differenceInDays(
                                                new Date(),
                                                new Date(x.qualifies_at), // todo here
                                            )}{' '}
                                            days ago
                                        </BodySmallEmphasized>
                                    </>
                                )}
                        </BodySmallRegular>
                    )}
                />
                <Column
                    field="first_bank_account_id"
                    header="First bank account"
                    style={{ width: '4rem', verticalAlign: 'top' }}
                    body={(x: DirectDepositRewardContext) => (
                        <BodySmallRegular style={{ fontSize: '0.75rem' }}>
                            First bank account:{' '}
                            <a
                                href={syncteraAccountUrl(
                                    x.first_synctera_account_id,
                                )}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {x.first_synctera_account_id}
                            </a>
                            <br />
                            Account created: {x.first_bank_account_created_at}
                            <br />
                            <br />
                            Qualification ends: {x.dd_range_end}
                            <br />
                        </BodySmallRegular>
                    )}
                />
                <Column
                    field="money_map_finalized_for_action_at"
                    header="Money map"
                    style={{ width: '4rem', verticalAlign: 'top' }}
                    body={(x: DirectDepositRewardContext) =>
                        x.program_member_type === 'new' ? (
                            <BodySmallRegular style={{ fontSize: '0.75rem' }}>
                                Active money map created:{' '}
                                {formatQualification(
                                    x.money_map_finalized_for_action_at ??
                                        'n/a',
                                    x.money_map_finalized_for_action_at != null,
                                )}{' '}
                                <br />
                                <a
                                    href={`/membership/${x.membership_id}?dt=9`}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    details
                                </a>
                                <br />
                            </BodySmallRegular>
                        ) : (
                            <BodySmallRegular>legacy - n/a</BodySmallRegular>
                        )
                    }
                />
                <Column
                    field="latest_dd_created_at"
                    header="Direct deposit"
                    style={{ width: '4rem', verticalAlign: 'top' }}
                    body={(x: DirectDepositRewardContext) => (
                        <BodySmallRegular style={{ fontSize: '0.75rem' }}>
                            {(x.cumulative_sum_30_days ?? 0) >= 200000
                                ? 'DD $2,000 qualified at:'
                                : 'Latest direct deposit:'}{' '}
                            {formatQualification(
                                x.latest_dd_created_at ?? 'n/a',
                                (x.cumulative_sum_30_days ?? 0) >= 200000,
                            )}
                            <br />
                            DD cumulative sum 30 days:{' '}
                            {x.cumulative_sum_30_days !== undefined &&
                            x.cumulative_sum_30_days !== null
                                ? formatQualification(
                                      centsToFormattedDollars(
                                          x.cumulative_sum_30_days,
                                      ),
                                      x.cumulative_sum_30_days >= 200000,
                                  )
                                : 'n/a'}
                            <br /># DD trx: {x.count_dd_trx_30_days}
                            <br />
                            <RenderDirectDepositTransactions data={x} />
                            <br />
                        </BodySmallRegular>
                    )}
                />
            </DataTable>
            <GroveSimpleModal
                isOpen={selectedNotFinalizedReward != null}
                onClose={() => {
                    setSelectedNotFinalizedReward(null);
                }}
            >
                <SmoothieScrollView maxHeight="80vh" width="70vw">
                    {selectedNotFinalizedReward != null && (
                        <ManualDirectDepositRewardButtonAndPopup
                            notFinalizedReward={selectedNotFinalizedReward}
                        />
                    )}
                </SmoothieScrollView>
            </GroveSimpleModal>
        </>
    );
};

const formatQualification = (
    text: string,
    meetsQualification: boolean,
): React.ReactNode => {
    if (meetsQualification) {
        return (
            <BodySmallEmphasized color="$green900">{text}</BodySmallEmphasized>
        );
    }
    return <BodySmallRegular>{text}</BodySmallRegular>;
};

interface RewardTrackingDisplayDto {
    id: string;
    idempotency_key: string;
    reward_program: string;
    membership_id: string;
    reward_transaction_uuid?: string;
    do_not_pay: boolean;
    note?: string;
    context: object; // Prisma.JsonValue;
    created_at: string;

    // Member specific details (debugging)
    primary_user_id: string;
    primary_name: string;
    primary_cancellation_date?: string;
    partner_user_id?: string;
    partner_name?: string;
    partner_cancellation_date?: string;
    membership_created_at?: string;
}

const RenderDirectDepositTransactions = ({
    data,
}: {
    data: DirectDepositRewardContext;
}) => {
    return (
        <YStack>
            <DataTable
                key={`sub_dd_table_${data.membership_id}`}
                value={data.json_agg_dd_trx ?? []}
                size="small"
                tableStyle={{ width: '100%' }}
                emptyMessage="All rows filtered out"
            >
                <Column
                    field="transaction_uuid"
                    header="Uuid"
                    body={(x: DirectDepositTransaction) => (
                        <a
                            href={syncteraPostedTransactionUrl(
                                x.transaction_uuid,
                            )}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {x.transaction_uuid.substring(0, 3)}..
                            {x.transaction_uuid.slice(-3)}
                        </a>
                    )}
                />
                <Column
                    field="posted_date"
                    header="Posted date"
                    body={(x: DirectDepositTransaction) => (
                        <>
                            {formatUTCDateToISODateString(
                                parseISO(x.posted_date),
                            )}
                        </>
                    )}
                />
                <Column
                    field="created_at"
                    header="Created at"
                    body={(x: DirectDepositTransaction) => (
                        <>
                            {format(parseISO(x.created_at), 'yyyy-MM-dd HH:mm')}
                        </>
                    )}
                />
                <Column
                    field="amount_cents"
                    header="Amount"
                    body={(x: DirectDepositTransaction) => (
                        <>{centsToFormattedDollars(x.amount_cents)}</>
                    )}
                />
                <Column field="sec_code" header="SEC code" />
                <Column field="description" header="Description" />
                <Column field="source_company_name" header="Company name" />
            </DataTable>
        </YStack>
    );
};

interface AdminBankOpsManualDirectDepositRewardPayoutRequestDto {
    context: DirectDepositRewardContext;
    doNotPay: boolean;
    note?: string;
    execute: boolean;
}

interface AdminBankOpsManualDirectDepositRewardPayoutResponseDto {
    // Intentionally blank; backend response shape to be formally defined
}

const useManualDirectDepositRewardPayout = requestBuilder.mutation<
    AdminBankOpsManualDirectDepositRewardPayoutResponseDto,
    AdminBankOpsManualDirectDepositRewardPayoutRequestDto
>({
    endpoint: '/api/admin/bank-ops/direct-deposit-reward/payout-manual',
    method: 'POST',
});

const ManualDirectDepositRewardButtonAndPopup = ({
    notFinalizedReward,
}: {
    notFinalizedReward: DirectDepositRewardContext;
}) => {
    const adminUser = useAtomValue(adminUserAtom);
    const defaultMode: PayoutMode = notFinalizedReward.qualifies_for_dd_bonus
        ? 'post-reward'
        : !notFinalizedReward.qualification_period_open
          ? 'log-non-qualification'
          : 'log-non-qualification-unexpected';
    const [doNotPay, setDoNotPay] = useState(defaultMode !== 'post-reward');

    const [note, setNote] = useState(
        `${adminUser.email}: ${defaultMode} for $250 DD promo`,
    );
    const [manualPayoutButtonDisabled, setManualPayoutButtonDisabled] =
        useState<boolean>(false);
    const [isManualPayoutConfirmModalOpen, setIsManualPayoutConfirmModalOpen] =
        useState(false);
    const [isManualPayoutExecute, setIsManualPayoutExecute] = useState(false);
    const [manualPayoutResult, setManualPayoutResult] =
        useState<AdminBankOpsManualDirectDepositRewardPayoutResponseDto | null>(
            null,
        );

    const { mutateAsync: manualDirectDepositRewardPayout } =
        useManualDirectDepositRewardPayout();

    const handleManualPayoutSubmit = async () => {
        setManualPayoutButtonDisabled(true);
        setIsManualPayoutConfirmModalOpen(false);

        const manualPayoutResult = await manualDirectDepositRewardPayout({
            body: {
                context: notFinalizedReward,
                doNotPay,
                note,
                execute: isManualPayoutExecute,
            },
        });
        setManualPayoutResult(manualPayoutResult);
        if (isManualPayoutExecute) {
            notFinalizedReward.decision_logged_in_grove = true;
        }
    };

    return (
        <>
            <YStack marginTop={32}>
                <XStack
                    backgroundColor="$gray200"
                    padding="15px"
                    gap={20}
                    justifyContent="space-between"
                >
                    <YStack gap={10}>
                        <H2Regular>
                            {doNotPay ? 'Log non-qualification' : 'Post reward'}
                        </H2Regular>
                        <BodySmallRegular>
                            Default mode: {defaultMode}
                        </BodySmallRegular>
                    </YStack>
                    <Button
                        onPress={(_) => {
                            setDoNotPay(!doNotPay);
                            setNote(
                                `${adminUser.email}: defaultMode=${defaultMode} - MODE SWITCHED - doNotPay=${doNotPay}`,
                            );
                        }}
                        variant="primary"
                        tone="danger"
                        width="auto"
                        size="small"
                    >
                        Switch payout mode
                    </Button>
                </XStack>
                <br />
                <br />
                <LabelRegular marginLeft={16}>Reward note:</LabelRegular>
                <Input
                    value={note}
                    onChangeText={setNote}
                    placeholder="Enter note"
                />

                <BodySmallRegular marginVertical={16}>
                    This should only be triggered by Fruitful Bank Ops.
                </BodySmallRegular>
                {!doNotPay &&
                    notFinalizedReward.qualifying_direct_deposit_synctera_account_id !=
                        null && (
                        <div>
                            <a
                                href={syncteraAccountUrl(
                                    notFinalizedReward.qualifying_direct_deposit_synctera_account_id,
                                )}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Go to Synctera account that will receive the
                                reward
                            </a>
                        </div>
                    )}
                <br />
                <br />

                <XStack justifyContent="center" gap={10}>
                    <Button
                        onPress={(_) => {
                            setIsManualPayoutExecute(true);
                            setIsManualPayoutConfirmModalOpen(true);
                        }}
                        disabled={manualPayoutButtonDisabled}
                        variant="primary"
                        tone="positive"
                        width="auto"
                        size="tiny"
                    >
                        {doNotPay
                            ? 'Log reward non-qualification (execute=true)'
                            : 'Issue direct deposit reward (execute=true)'}
                    </Button>

                    <Button
                        onPress={(_) => {
                            setIsManualPayoutExecute(false);
                            setIsManualPayoutConfirmModalOpen(true);
                        }}
                        disabled={manualPayoutButtonDisabled}
                        variant="secondary"
                        width="auto"
                        size="tiny"
                    >
                        {doNotPay
                            ? 'Log reward non-qualification (execute=false)'
                            : 'Issue direct deposit reward (execute=false)'}
                    </Button>
                </XStack>
                <BodyMediumRegular></BodyMediumRegular>
                {manualPayoutResult != null && (
                    <>
                        <BodyMediumRegular>COMPLETE</BodyMediumRegular>
                        <BodyMediumRegular>
                            {JSON.stringify(manualPayoutResult, null, 2)}
                        </BodyMediumRegular>
                    </>
                )}
            </YStack>

            <ConfirmModal
                isOpen={isManualPayoutConfirmModalOpen}
                onClose={() => {
                    setIsManualPayoutConfirmModalOpen(false);
                    setManualPayoutResult(null);
                }}
                onConfirm={handleManualPayoutSubmit}
            />
        </>
    );
};

const DirectDepositRewardDecisionLoggedTable = ({
    data,
}: {
    data: RewardTrackingDisplayDto[];
}) => {
    if (data.length === 0) {
        return <BodyMediumRegular>No rows</BodyMediumRegular>;
    }

    return (
        <DataTable
            dataKey="id"
            value={data}
            size="small"
            tableStyle={{ width: '100%' }}
        >
            <Column
                field="membership_id"
                header="Membership"
                style={{ width: '6rem', verticalAlign: 'top' }}
                body={(x: RewardTrackingDisplayDto) => {
                    const programMemberType = (
                        x.context as { program_member_type?: string }
                    ).program_member_type;
                    return (
                        <BodySmallRegular style={{ fontSize: '0.75rem' }}>
                            Program member type:{' '}
                            {programMemberType === 'new' ? (
                                <BodySmallEmphasized color="$green900">
                                    {programMemberType}
                                </BodySmallEmphasized>
                            ) : (
                                <BodySmallEmphasized color="$red900">
                                    {programMemberType}
                                </BodySmallEmphasized>
                            )}
                            <br />
                            MembershipId: {x.membership_id}
                            <br />
                            Membership created:{' '}
                            {x.membership_created_at ?? 'n/a'}
                            <br />
                            Primary member: {x.primary_name}
                            <br />
                            {x.partner_user_id != null && (
                                <>
                                    Partner member: {x.partner_name}
                                    <br />
                                </>
                            )}
                        </BodySmallRegular>
                    );
                }}
            />
            <Column field="note" header="Note" />
            <Column field="created_at" header="Created at" />
            <Column
                field="id"
                header="Posted reward transaction"
                body={(x: RewardTrackingDisplayDto) => (
                    <BodySmallRegular style={{ fontSize: '0.75rem' }}>
                        {x.reward_transaction_uuid == null ? (
                            <>no reward issued</>
                        ) : (
                            <a
                                href={syncteraPostedTransactionUrl(
                                    x.reward_transaction_uuid,
                                )}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {x.reward_transaction_uuid}
                            </a>
                        )}
                    </BodySmallRegular>
                )}
            />
            <Column field="idempotency_key" header="Idempotency key" />
        </DataTable>
    );
};
